import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { Job } from '@intranet/modules/jobs/job.types';
import { Subject, of, switchMap, tap } from 'rxjs';
import { ActionService } from '@intranet/modules/actions';
import { JobService } from '../../job.service';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialog } from '@intranet/shared/alert/alert-dialog.component';
import { PageEvent } from '@angular/material/paginator';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { TogglService, TogglRunningJob } from '@intranet/modules/toggl';
import { Subscription } from "rxjs";
import { LoginService } from '@intranet/modules/login';


@Component({
    selector: 'jobs-table',
    templateUrl: './jobs-table.component.html',
    styleUrls: ['./jobs-table.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
            state('expanded', style({ height: '*', visibility: 'visible' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
        ])
    ]
})
export class JobsTableComponent implements OnChanges, OnDestroy {
    @Input() jobs: Job[];
    @Input() status: string;
    @Input() hideHeader = false;
    @Input() showClientColumn: boolean = false;
    @Input() sortBy = "type";
    @Input() sortDir: string;
    @Input() pageIndex = 0;
    @Input() totalResults = 0;
    @Output() edit: EventEmitter<any> = new EventEmitter();
    @Output() paginate = new EventEmitter<PageEvent>();
    @Output() sort = new EventEmitter<string>();
    @Output() archived = new EventEmitter<{ job?: Job, error?: string }>();
    @Input() linkJobs: boolean = false;
    @Output() linkJob = new EventEmitter<Job>();
    @Input() pinnedJobs: number[] = [];
    @Output() pin = new EventEmitter<Job>();
    @Output() unpin = new EventEmitter<Job>();
    @Input() bulkColumnEnabled = false;
    @Input() selectedJobs: Job[] = [];
    @Output() selectionChanged = new EventEmitter<Job[]>();
    @Output() bulkAction = new EventEmitter<{ action: string; jobs: Job[] }>();
    expandedJobIds = new Set<number>();
    runningJobId: number | null = null;
    private _togglSub = new Subscription();
    @Output() toggleBillable = new EventEmitter<Job>();
    @Output() deleteJob = new EventEmitter<Job>();

    protected previousSortBy: string;
    /** Sort column that the currently-displayed `jobs` are actually ordered by.
     *  Updated only when `jobs` changes, so the group dividers track the data on
     *  screen — not the requested `sortBy`, which flips on click before the
     *  re-sorted rows arrive (which briefly drew dividers on every row). */
    appliedSortBy = 'type';
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    isPinned(jobId: number): boolean {
        return this.pinnedJobs.includes(jobId);
    }

    constructor(
        public dialog: MatDialog,
        private _router: Router,
        private _jobService: JobService,
        private _actionService: ActionService,
        private togglService: TogglService,
        private loginService: LoginService,
        private cd: ChangeDetectorRef
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        // Sync the divider's sort key to the data only when the rows actually
        // change, so dividers appear together with the re-sorted data rather
        // than flashing against the previous ordering.
        if (changes['jobs']) {
            this.appliedSortBy = this.sortBy;
        }
    }

    ngOnInit() {
        this._togglSub.add(
            this.togglService.togglRunningJob$.subscribe((r: TogglRunningJob | null) => {
                this.runningJobId = r ? r.jobId : null;
            })
        );
    }

    ngOnDestroy() {
        this._unsubscribeAll.next('');
        this._unsubscribeAll.complete();
        this._togglSub.unsubscribe();
    }

    showMore() {
        return this.jobs.length % 50 === 0;
    }

    onPaginate(e: PageEvent) {
        this.paginate.emit(e);
    }

     onToggleBillable(job: Job) {
        this.toggleBillable.emit(job);
    }

    onDelete(job: Job) {
        this.deleteJob.emit(job);
    }

    onSelect(job: Job) {
        if (this.linkJobs) {
            this.linkJob.emit(job);
        } else {
            this._actionService.clearFilterQuery();
            this._router.navigate([`/jobs/detail/${job.jobId}`]);
        }
    }

    onSort(prop: string) {
        this.sort.emit(prop);
    }

    onEdit(job: Job) {
        this.edit.emit(job);
    }

    // Animation trigger for row expansion
    animations = [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
            state('expanded', style({ height: '*', visibility: 'visible' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
        ])
    ];

    toggleRow(job: Job, event: Event): void {
        event.stopPropagation();

        const isAlreadyExpanded = this.expandedJobIds.has(job.jobId);

        if (isAlreadyExpanded) {
            // Collapse the same row if it's already open
            this.expandedJobIds.delete(job.jobId);
        } else {
            // Collapse others, then expand the clicked one
            this.expandedJobIds.clear();
            this.expandedJobIds.add(job.jobId);
        }
    }

    isExpanded(job: Job): boolean {
        return this.expandedJobIds.has(job.jobId);
    }

    // Calculate the colspan based on the header. For example:
    getColspan(): number {
      let count = 0;
      // According to the header definition:
      // 1. Status icon column
      // 2. Owner
      // 3. (Client column if showClientColumn)
      // 4. Number
      // 5. Job Title
      // 6. Status
      // 7. Type
      // 8. Extra column (if !linkJobs)
      // 9. Extra column (if !linkJobs)
      count = 1 + 1 + (this.showClientColumn ? 1 : 0) + 1 + 1 + 1 + 1 + 1 + 1 + 1 + (this.bulkColumnEnabled ? 1 : 0);
      if (!this.linkJobs) {
        count += 2;
      }
      return count;
    }

    onArchive(job: Job) {
        const dialogRef = this.dialog.open(AlertDialog, {
            width: '320px',
            data: {
                title: 'Are you sure?',
                message: `You are about to ${this.status === 'archive' ? 'un-archive':'archive'} this job. Are you sure?`,
                confirm: true,
                confirmTitle: 'Yes',
            },
        });

        dialogRef
            .afterClosed()
            .pipe(
                switchMap((result) => {
                    if (!result) {
                        return of(null);
                    }
                    return this._jobService.archive(
                        job.jobId,
                        this.status === 'archive' ? false : true
                    );
                }),
                tap(response => {
                    if (response && response.error) {
                        this.archived.emit({ error: response.error });
                    }

                    this.archived.emit({ job });
                })
            )
            .subscribe();
    }

    startTimer(job: Job) {
        this.togglService.startTimerForJob(job.jobId).subscribe({
            next: (result) => {
            }
        })
    }

    isAllSelected(): boolean {
        return (
            this.bulkColumnEnabled &&
            this.jobs.length > 0 &&
            this.selectedJobs.length === this.jobs.length
        );
    }

    isSomeSelected(): boolean {
        return (
            this.bulkColumnEnabled &&
            this.selectedJobs.length > 0 &&
            this.selectedJobs.length < this.jobs.length
        );
    }

    isSelected(job: Job): boolean {
        return this.selectedJobs.some((j) => j.jobId === job.jobId);
    }

    onBulkSelect(job: Job, event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        if (checked) {
            this.selectedJobs = [...this.selectedJobs, job];
        } else {
            this.selectedJobs = this.selectedJobs.filter((j) => j.jobId !== job.jobId);
        }
        this.selectionChanged.emit(this.selectedJobs);
    }

    onBulkSelectAll(event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        this.selectedJobs = checked ? [...this.jobs] : [];
        this.selectionChanged.emit(this.selectedJobs);
    }

    public get selectedJob(): Job | null {
        return this.selectedJobs.length === 1 ? this.selectedJobs[0] : null;
    }


    get hasArchivable(): boolean {
        return this.selectedJobs.some(j => !j.archive && j.canArchive);
    }

    get hasUnarchivable(): boolean {
        return this.selectedJobs.some(j => j.archive && j.canArchive);
    }

    public isBillable(job: Job): boolean {
        return typeof job.isBillable === 'string'
            ? job.isBillable === 'true'
            : job.isBillable;
    }

    private _isBillable(job: Job): boolean {
        return typeof job.isBillable === 'string'
            ? job.isBillable === 'true'
            : job.isBillable;
    }

    get hasBillable(): boolean {
        return this.selectedJobs.some(j => !this._isBillable(j));
    }

    get hasNonBillable(): boolean {
        return this.selectedJobs.some(j => this._isBillable(j));
    }

    onBulkAction(action: string): void {
          let jobsToAct: Job[] = [];

        switch (action) {
            case 'billable':
                // only pick those that are currently non-billable
                jobsToAct = this.selectedJobs.filter(j => !this._isBillable(j));
                break;

            case 'non-billable':
                // only pick those that are currently billable
                jobsToAct = this.selectedJobs.filter(j => this._isBillable(j));
                break;

            case 'archived':
                jobsToAct = this.selectedJobs.filter(j => !j.archive && j.canArchive);
                break;

            case 'live':
                jobsToAct = this.selectedJobs.filter(j => j.archive && j.canArchive);
                break;
        }

        if (jobsToAct.length) {
            this.bulkAction.emit({ action, jobs: jobsToAct });
        }
    }
}
